import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AIIcon,
  CheckCircle as ActiveIcon,
  Cancel as RevokedIcon,
  AccessTime as ExpiredIcon,
  Person as UsedIcon,
  Edit as EditIcon,
  Upgrade as UpgradeIcon,
  PersonOff as PersonOffIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import AdminLayout from '../../components/templates/layout/AdminLayout';
import PlatformDialog from '../../components/molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../components/molecules/PlatformDialog/PlatformDialogTitle';

const CONTRACT_FEATURE_OPTIONS = [
  { key: 'smartScore', label: 'SmartScore' },
  { key: 'aiQualitativeAnalysis', label: 'Análise Qualitativa IA' },
  { key: 'shareOfVoice', label: 'Share of Voice' },
  { key: 'briefingAssistant', label: 'Assistente de Briefing' },
  { key: 'newsletter', label: 'Newsletter' },
  { key: 'publicDashboard', label: 'Dashboard Público' },
  { key: 'powerBi', label: 'Power BI' }
];

const MIA_FEATURE_KEYS = [
  'sentimentAI',
  'sentimentPropagation',
  'autoTags',
  'similarityDetection',
  'smartScraping',
  'contentSummary',
  'kpiaAnalysis'
];

const MIA_FEATURE_OPTIONS = [
  { key: 'sentimentAI', label: 'Análise de Sentimento (LLM)' },
  { key: 'sentimentPropagation', label: 'Propagação Automática de Sentimento' },
  { key: 'autoTags', label: 'Automação de Tags por Briefing' },
  { key: 'similarityDetection', label: 'Agrupamento de Similares' },
  { key: 'contentSummary', label: 'Resumo de Conteúdo IA' },
  { key: 'kpiaAnalysis', label: 'Valoração Multimodal KPIA' }
];

const normalizeMIAFeaturesForForm = (features, hasAI = false) => Object.fromEntries(
  MIA_FEATURE_KEYS.map((key) => [
    key,
    hasAI ? (typeof features?.[key] === 'boolean' ? features[key] : true) : false
  ])
);

const buildPackageFeatures = (hasAI = false, overrides = {}) => ({
  smartScore: true,
  aiQualitativeAnalysis: hasAI === true,
  shareOfVoice: true,
  briefingAssistant: hasAI === true,
  newsletter: true,
  publicDashboard: true,
  powerBi: false,
  ...overrides
});

const PLAN_PRESETS = {
  Starter: { maxLinks: 100, maxBrands: 1, hasAI: true, features: buildPackageFeatures(true) },
  Pro: { maxLinks: 300, maxBrands: 3, hasAI: true, features: buildPackageFeatures(true) },
  Business: { maxLinks: 700, maxBrands: 7, hasAI: true, features: buildPackageFeatures(true) },
  Enterprise: { maxLinks: 2000, maxBrands: 15, hasAI: true, features: buildPackageFeatures(true) },
  Custom: {}
};

const mergePackageFeatures = (features, hasAI = false) => ({
  ...buildPackageFeatures(hasAI),
  ...(features || {})
});

const MIATokenManagementPage = () => {
  const [tokens, setTokens] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, used: 0, expired: 0, revoked: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editPlanDialogOpen, setEditPlanDialogOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedEditUser, setSelectedEditUser] = useState(null); // { userId, name, email, plan }
  const [editPlan, setEditPlan] = useState({
    planName: 'Starter',
    maxLinks: 100,
    maxBrands: 1,
    hasAI: true,
    durationDays: 0,
    features: buildPackageFeatures(true),
    miaFeatures: normalizeMIAFeaturesForForm({}, true),
    notes: ''
  });
  const [editPlanLoading, setEditPlanLoading] = useState(false);
  const [editPlanSuccess, setEditPlanSuccess] = useState('');
  const [editPlanError, setEditPlanError] = useState('');

  // New token form
  const [newToken, setNewToken] = useState({
    clientName: '',
    clientEmail: '',
    expiresAt: '',
    notes: '',
    type: 'activation',
    plan: {
      name: 'Starter',
      maxLinks: 100,
      maxBrands: 1,
      hasAI: true,
      durationDays: 365,
      features: buildPackageFeatures(true)
    },
    features: {
      sentimentAI: true,
      sentimentPropagation: true,
      autoTags: true,
      similarityDetection: true,
      smartScraping: true,
      contentSummary: true,
      kpiaAnalysis: false
    }
  });

  const [formError, setFormError] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [copiedToken, setCopiedToken] = useState(false);

  // Users for autocomplete
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Activated users section
  const [activatedUsers, setActivatedUsers] = useState([]);
  const [activatedUsersLoading, setActivatedUsersLoading] = useState(false);
  const [activatedUsersSearch, setActivatedUsersSearch] = useState('');
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null); // { userId, name }
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeError, setRevokeError] = useState('');

  // Fetch tokens
  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/mia/admin/tokens', { params });
      setTokens(response.data.tokens);
      setTotalItems(response.data.pagination.total);
    } catch (error) {
      console.error('Erro ao carregar tokens:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/mia/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  // Fetch users for autocomplete
  const fetchUsers = useCallback(async (searchTerm = '') => {
    setUsersLoading(true);
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await api.get('/mia/admin/users', { params });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Fetch activated users
  const fetchActivatedUsers = useCallback(async (search = '') => {
    setActivatedUsersLoading(true);
    try {
      const res = await api.get('/mia/admin/activated-users', { params: { search, limit: 50 } });
      setActivatedUsers(res.data.users || []);
    } catch (e) {
      console.error('Erro ao carregar usuários ativados:', e);
    } finally {
      setActivatedUsersLoading(false);
    }
  }, []);

  // Revoke user plan
  const handleRevokePlan = async () => {
    if (!revokeTarget) return;
    setRevokeLoading(true);
    setRevokeError('');
    try {
      await api.post(`/mia/admin/users/${revokeTarget.userId}/revoke-plan`);
      setRevokeDialogOpen(false);
      setRevokeTarget(null);
      fetchActivatedUsers(activatedUsersSearch);
    } catch (e) {
      setRevokeError(e.response?.data?.error || 'Erro ao revogar plano');
    } finally {
      setRevokeLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
    fetchStats();
    fetchActivatedUsers();
  }, [fetchTokens, fetchStats, fetchActivatedUsers]);

  // Load users when dialog opens
  useEffect(() => {
    if (createDialogOpen && users.length === 0) {
      fetchUsers();
    }
  }, [createDialogOpen, fetchUsers, users.length]);

  // Create token
  const handleCreateToken = async () => {
    if (!newToken.clientName.trim()) {
      setFormError('Nome do cliente é obrigatório');
      return;
    }

    try {
      const payload = {
        clientName: newToken.clientName,
        clientEmail: newToken.clientEmail,
        expiresAt: newToken.expiresAt || null,
        notes: newToken.notes,
        type: newToken.type
      };

      if (selectedUser) {
        payload.userId = selectedUser._id;
      }

      if (newToken.type === 'activation') {
        payload.planName = newToken.plan.name;
        payload.maxLinks = Number(newToken.plan.maxLinks);
        payload.maxBrands = Number(newToken.plan.maxBrands);
        payload.hasAI = newToken.plan.hasAI;
        payload.durationDays = Number(newToken.plan.durationDays);
        payload.contractFeatures = newToken.plan.features;
        payload.miaFeatures = newToken.features;
      } else if (newToken.type === 'upgrade') {
        payload.planName = newToken.plan.name;
        payload.maxLinks = Number(newToken.plan.maxLinks);
        payload.maxBrands = Number(newToken.plan.maxBrands);
        payload.hasAI = newToken.plan.hasAI;
        payload.durationDays = Number(newToken.plan.durationDays);
        payload.contractFeatures = newToken.plan.features;
        payload.miaFeatures = newToken.features;
      } else {
        payload.features = newToken.features;
      }

      const response = await api.post('/mia/admin/tokens', payload);

      setCreatedToken(response.data.token);
      fetchTokens();
      fetchStats();
      fetchActivatedUsers();
      setFormError('');
    } catch (error) {
      setFormError(error.response?.data?.error || 'Erro ao criar token');
    }
  };

  // Revoke token
  const handleRevokeToken = async (tokenId) => {
    try {
      await api.post(`/mia/admin/tokens/${tokenId}/revoke`);
      fetchTokens();
      fetchStats();
    } catch (error) {
      console.error('Erro ao revogar token:', error);
    }
  };

  // Delete token
  const handleDeleteToken = async () => {
    if (!selectedToken) return;

    try {
      await api.delete(`/mia/admin/tokens/${selectedToken._id}`);
      setDeleteDialogOpen(false);
      setSelectedToken(null);
      fetchTokens();
      fetchStats();
    } catch (error) {
      console.error('Erro ao excluir token:', error);
    }
  };

  // Copy token to clipboard
  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  // Reset form
  const resetForm = () => {
    setNewToken({
      clientName: '',
      clientEmail: '',
      expiresAt: '',
      notes: '',
      type: 'activation',
      plan: {
        name: 'Starter',
        maxLinks: 100,
        maxBrands: 1,
        hasAI: true,
        durationDays: 365,
        features: buildPackageFeatures(true)
      },
      features: {
        sentimentAI: true,
        sentimentPropagation: true,
        autoTags: true,
        similarityDetection: true,
        smartScraping: true,
        contentSummary: true,
        kpiaAnalysis: false
      }
    });
    setSelectedUser(null);
    setFormError('');
    setCreatedToken(null);
  };

  // Open Edit Plan dialog for a used token's user
  const handleOpenEditPlan = (token) => {
    if (!token.userId) return;
    // token.userId pode ser um objeto populado ou um string ID
    const rawUserId = token.userId;
    const sourcePlan = rawUserId?.activation?.plan || token.plan || {};
    const resolvedUserId = (rawUserId && typeof rawUserId === 'object')
      ? (rawUserId._id?.toString() || rawUserId.toString())
      : rawUserId;
    setSelectedEditUser({
      userId: resolvedUserId,
      name: token.clientName || rawUserId?.name,
      email: token.clientEmail || rawUserId?.email
    });
    setEditPlan({
      planName: sourcePlan.name || 'Starter',
      maxLinks: sourcePlan.maxLinks || 100,
      maxBrands: sourcePlan.maxBrands || 1,
      hasAI: sourcePlan.hasAI || false,
      durationDays: 0,
      features: mergePackageFeatures(sourcePlan.features, sourcePlan.hasAI || false),
      miaFeatures: normalizeMIAFeaturesForForm(sourcePlan.miaFeatures, sourcePlan.hasAI || false),
      notes: ''
    });
    setEditPlanSuccess('');
    setEditPlanError('');
    setEditPlanDialogOpen(true);
  };

  const handleSaveEditPlan = async () => {
    if (!selectedEditUser?.userId) return;
    setEditPlanLoading(true);
    setEditPlanError('');
    setEditPlanSuccess('');
    try {
      const contractFeatures = mergePackageFeatures(editPlan.features, editPlan.hasAI);
      const miaFeatures = normalizeMIAFeaturesForForm(editPlan.miaFeatures, editPlan.hasAI);
      const payload = {
        ...editPlan,
        maxLinks: Number(editPlan.maxLinks),
        maxBrands: Number(editPlan.maxBrands),
        durationDays: Number(editPlan.durationDays),
        features: contractFeatures,
        contractFeatures,
        miaFeatures
      };
      const res = await api.put(`/mia/admin/users/${selectedEditUser.userId}/plan`, payload);
      const updatedPlan = res.data.user?.plan || {};
      const nextPlan = {
        name: updatedPlan.name || editPlan.planName,
        maxLinks: updatedPlan.maxLinks ?? payload.maxLinks,
        maxBrands: updatedPlan.maxBrands ?? payload.maxBrands,
        hasAI: updatedPlan.hasAI ?? payload.hasAI,
        planExpiresAt: updatedPlan.planExpiresAt,
        features: mergePackageFeatures(updatedPlan.features || contractFeatures, updatedPlan.hasAI ?? payload.hasAI),
        miaFeatures: normalizeMIAFeaturesForForm(updatedPlan.miaFeatures || miaFeatures, updatedPlan.hasAI ?? payload.hasAI)
      };

      setEditPlan(prev => ({
        ...prev,
        planName: nextPlan.name,
        maxLinks: nextPlan.maxLinks,
        maxBrands: nextPlan.maxBrands,
        hasAI: nextPlan.hasAI,
        durationDays: 0,
        features: nextPlan.features,
        miaFeatures: nextPlan.miaFeatures
      }));
      setActivatedUsers(prev => prev.map(user => (
        user._id?.toString() === selectedEditUser.userId?.toString()
          ? { ...user, plan: { ...user.plan, ...nextPlan } }
          : user
      )));
      setEditPlanSuccess(res.data.message || 'Plano atualizado!');
      await Promise.allSettled([
        fetchTokens(),
        fetchActivatedUsers(activatedUsersSearch)
      ]);
    } catch (err) {
      setEditPlanError(err.response?.data?.error || 'Erro ao atualizar plano');
    } finally {
      setEditPlanLoading(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (event, user) => {
    setSelectedUser(user);
    if (user) {
      setNewToken(prev => ({
        ...prev,
        clientName: user.name,
        clientEmail: user.email
      }));
    }
  };

  // Status chip
  const getStatusChip = (status) => {
    const configs = {
      active: { label: 'Disponivel', color: 'success', icon: <ActiveIcon fontSize="small" /> },
      used: { label: 'Em uso', color: 'primary', icon: <UsedIcon fontSize="small" /> },
      expired: { label: 'Expirado', color: 'warning', icon: <ExpiredIcon fontSize="small" /> },
      revoked: { label: 'Revogado', color: 'error', icon: <RevokedIcon fontSize="small" /> }
    };
    const config = configs[status] || configs.active;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AIIcon sx={{ fontSize: 32, color: '#8B5CF6', mr: 1.5 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Tokens MIA
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerenciamento de tokens de ativacao do Mensure IA
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">Total</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {stats.active}
                </Typography>
                <Typography variant="body2" color="text.secondary">Disponiveis</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {stats.used}
                </Typography>
                <Typography variant="body2" color="text.secondary">Em uso</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {stats.expired + stats.revoked}
                </Typography>
                <Typography variant="body2" color="text.secondary">Inativos</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Buscar por token, cliente ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 300 }}
            />

            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 150 }}
            >
              <option value="">Todos</option>
              <option value="active">Disponivel</option>
              <option value="used">Em uso</option>
              <option value="expired">Expirado</option>
              <option value="revoked">Revogado</option>
            </TextField>

            <Box sx={{ flexGrow: 1 }} />

            <IconButton onClick={() => { fetchTokens(); fetchStats(); }}>
              <RefreshIcon />
            </IconButton>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { resetForm(); setCreateDialogOpen(true); }}
              sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
            >
              Gerar Token
            </Button>
          </Box>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Token</TableCell>
                    <TableCell>Tipo / Plano</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Criado em</TableCell>
                    <TableCell>Ativado em</TableCell>
                    <TableCell>Expira em</TableCell>
                    <TableCell align="right">Acoes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.map((token) => (
                    <TableRow key={token._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                          >
                            {token.token}
                          </Typography>
                          <Tooltip title={copiedToken ? 'Copiado!' : 'Copiar token'}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyToken(token.token)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            token.type === 'upgrade' ? `Upgrade · ${token.plan?.name || ''}` :
                              token.type === 'activation' ? `Ativação · ${token.plan?.name || 'Starter'}` : 'Feature'
                          }
                          size="small"
                          color={token.type === 'activation' ? 'secondary' : token.type === 'upgrade' ? 'warning' : 'default'}
                          variant="outlined"
                          icon={token.type === 'upgrade' ? <UpgradeIcon /> : undefined}
                        />
                        {(token.type === 'activation' || token.type === 'upgrade') && token.plan?.maxLinks && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {token.plan.maxLinks} links · {token.plan.maxBrands} marcas · {token.plan.hasAI ? 'com IA' : 'sem IA'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {token.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {token.clientEmail || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(token.status)}</TableCell>
                      <TableCell>{formatDate(token.createdAt)}</TableCell>
                      <TableCell>{formatDate(token.activatedAt)}</TableCell>
                      <TableCell>{formatDate(token.expiresAt)}</TableCell>
                      <TableCell align="right">
                        {/* Editar Plano — apenas para tokens 'used' com userId */}
                        {token.status === 'used' && token.userId && (
                          <Tooltip title="Editar Plano">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditPlan(token)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {token.status === 'active' && (
                          <Tooltip title="Revogar">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRevokeToken(token._id)}
                            >
                              <BlockIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setSelectedToken(token); setDeleteDialogOpen(true); }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tokens.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          Nenhum token encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Itens por pagina"
              />
            </>
          )}
        </TableContainer>

        {/* Create Token Dialog (Padronizado) */}
        <PlatformDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <PlatformDialogTitle
            title={createdToken ? 'Token Criado!' : 'Gerar Novo Token MIA'}
            subtitle={createdToken ? 'Envie este código ao cliente para ativação.' : 'Gere uma chave de acesso para novos clientes.'}
            icon={<AIIcon />}
            onClose={() => setCreateDialogOpen(false)}
          />
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            {createdToken ? (
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Alert severity="success" variant="filled" sx={{ mb: 3, borderRadius: 2 }}>
                  Token gerado com sucesso!
                </Alert>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    borderRadius: 3,
                    border: '2px dashed',
                    borderColor: 'primary.main'
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.15em', wordBreak: 'break-all' }}
                  >
                    {createdToken.token}
                  </Typography>
                  <Tooltip title={copiedToken ? 'Copiado!' : 'Copiar para a Área de Transferência'}>
                    <IconButton 
                      onClick={() => handleCopyToken(createdToken.token)}
                      sx={{ bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
                    >
                      <CopyIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                </Paper>
                <Box sx={{ mt: 3, textAlign: 'left' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Cliente:</strong> {createdToken.clientName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Expiração:</strong> {formatDate(createdToken.expiresAt)}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {formError && (
                  <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                    {formError}
                  </Alert>
                )}

                <FormControl fullWidth size="small">
                  <InputLabel>Tipo do Token</InputLabel>
                  <Select
                    value={newToken.type}
                    label="Tipo do Token"
                    onChange={(e) => setNewToken({ ...newToken, type: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="activation">🔑 Ativação de Conta (Pacote Completo)</MenuItem>
                    <MenuItem value="upgrade">⬆️ Upgrade de Plano (Alteração de Limites)</MenuItem>
                    <MenuItem value="feature">⚡ Feature MIA Extra (IA Adicional)</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                    {newToken.type === 'activation' ? 'Ativa a conta do usuário na plataforma pela primeira vez.' :
                      newToken.type === 'upgrade' ? 'Atualiza limites de uma conta que já possui plano ativo.' :
                        'Libera funcionalidades específicas de Inteligência Artificial.'}
                  </Typography>
                </FormControl>

                {/* Campos de Plano (para activation e upgrade) */}
                {(newToken.type === 'activation' || newToken.type === 'upgrade') && (
                  <Box sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'secondary.main' + '40' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="secondary.main" mb={1.5}>
                      Configuração do Plano
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                          <Select
                            value={newToken.plan.name}
                            label="Plano"
                            onChange={(e) => {
                              const preset = PLAN_PRESETS[e.target.value] || {};
                              setNewToken(prev => ({
                                ...prev,
                                plan: {
                                  ...prev.plan,
                                  name: e.target.value,
                                  ...preset,
                                  features: mergePackageFeatures(preset.features || prev.plan.features, preset.hasAI ?? prev.plan.hasAI)
                                }
                              }));
                            }}
                            sx={{ borderRadius: 2 }}
                          >
                            <MenuItem value="Starter">Starter — 100 pub/mês · 1 marca · R$ 397/mês</MenuItem>
                            <MenuItem value="Pro">Pro — 300 pub/mês · 3 marcas · R$ 797/mês</MenuItem>
                            <MenuItem value="Business">Business — 700 pub/mês · 7 marcas · R$ 1.397/mês</MenuItem>
                            <MenuItem value="Enterprise">Enterprise — 2.000 pub/mês · 15 marcas · R$ 2.197/mês</MenuItem>
                            <MenuItem value="Custom">Custom — Configurar manualmente</MenuItem>
                          </Select>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Links" type="number"
                          value={newToken.plan.maxLinks}
                          onChange={(e) => setNewToken(prev => ({ ...prev, plan: { ...prev.plan, maxLinks: e.target.value } }))}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Marcas" type="number"
                          value={newToken.plan.maxBrands}
                          onChange={(e) => setNewToken(prev => ({ ...prev, plan: { ...prev.plan, maxBrands: e.target.value } }))}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth size="small" label="Duração (dias)" type="number"
                          value={newToken.plan.durationDays}
                          onChange={(e) => setNewToken(prev => ({ ...prev, plan: { ...prev.plan, durationDays: e.target.value } }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 2, mt: 1 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={newToken.plan.hasAI}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setNewToken(prev => ({
                                    ...prev,
                                    plan: {
                                      ...prev.plan,
                                      hasAI: checked,
                                      features: {
                                        ...prev.plan.features,
                                        aiQualitativeAnalysis: checked,
                                        briefingAssistant: checked
                                      }
                                    },
                                    features: normalizeMIAFeaturesForForm(prev.features, checked)
                                  }));
                                }}
                                color="secondary"
                              />
                            }
                            label={<Typography variant="subtitle2" fontWeight={700}>Ativar Módulos de Inteligência (MIA)</Typography>}
                          />
                          
                          {newToken.plan.hasAI && (
                            <Box sx={{ pl: 4, pt: 1 }}>
                              <Grid container spacing={1}>
                                {Object.entries({
                                  sentimentAI: 'Análise de Sentimento (LLM)',
                                  sentimentPropagation: 'Propagação Automática de Sentimento',
                                  autoTags: 'Automação de Tags por Briefing',
                                  similarityDetection: 'Agrupamento de Similares (Anti-Ruído)',
                                  smartScraping: 'MensureVision (Leitura em Sites Complexos)',
                                  contentSummary: 'Resumo de Conteúdo IA',
                                  kpiaAnalysis: 'Valoração Multimodal KPIA (Elite)'
                                }).map(([key, label]) => (
                                  <Grid item xs={12} sm={6} key={key}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={newToken.features[key]}
                                          onChange={(e) => setNewToken(prev => ({
                                            ...prev,
                                            features: { ...prev.features, [key]: e.target.checked }
                                          }))}
                                        />
                                      }
                                      label={<Typography variant="body2">{label}</Typography>}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                          <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 2, mt: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                              Módulos do pacote contratado
                            </Typography>
                            <Grid container spacing={1}>
                              {CONTRACT_FEATURE_OPTIONS.map(({ key, label }) => (
                                <Grid item xs={12} sm={6} key={key}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={newToken.plan.features?.[key] === true}
                                        onChange={(e) => setNewToken(prev => ({
                                          ...prev,
                                          plan: {
                                            ...prev.plan,
                                            features: {
                                              ...prev.plan.features,
                                              [key]: e.target.checked
                                            }
                                          }
                                        }))}
                                      />
                                    }
                                    label={<Typography variant="body2">{label}</Typography>}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  value={selectedUser}
                  onChange={handleUserSelect}
                  loading={usersLoading}
                  onInputChange={(event, value, reason) => {
                    if (reason === 'input') {
                      fetchUsers(value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecionar Usuario Cadastrado"
                      placeholder="Buscar por nome ou email..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText="Nenhum usuario encontrado"
                  sx={{ mb: 2 }}
                />

                <TextField fullWidth label="Nome do Cliente" value={newToken.clientName}
                  onChange={(e) => setNewToken({ ...newToken, clientName: e.target.value })}
                  required sx={{ mb: 2 }}
                  helperText={selectedUser ? "Preenchido automaticamente" : "Ou digite manualmente"}
                />

                <TextField fullWidth label="Email do Cliente" type="email" value={newToken.clientEmail}
                  onChange={(e) => setNewToken({ ...newToken, clientEmail: e.target.value })}
                  sx={{ mb: 2 }}
                  helperText={selectedUser ? "Preenchido automaticamente" : "Opcional"}
                />

                <TextField fullWidth label="Data de Expiracao do Código (opcional)" type="datetime-local"
                  value={newToken.expiresAt}
                  onChange={(e) => setNewToken({ ...newToken, expiresAt: e.target.value })}
                  InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
                  helperText="Quando este código expira (antes de ser usado)"
                />

                <TextField fullWidth label="Observacoes" multiline rows={2}
                  value={newToken.notes}
                  onChange={(e) => setNewToken({ ...newToken, notes: e.target.value })}
                  sx={{ mb: 2 }}
                />

                {/* Features (apenas para tokens tipo feature) */}
                {newToken.type === 'feature' && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Features Habilitadas
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {Object.entries({
                        sentimentAI: 'Analise de Sentimento',
                        sentimentPropagation: 'Propagacao de Sentimento',
                        autoTags: 'Tags Automaticas',
                        similarityDetection: 'Deteccao de Similaridade',
                        smartScraping: 'Scraping Inteligente',
                        contentSummary: 'Resumo Automatico'
                      }).map(([key, label]) => (
                        <FormControlLabel key={key}
                          control={
                            <Checkbox
                              checked={newToken.features[key]}
                              onChange={(e) => setNewToken({
                                ...newToken,
                                features: { ...newToken.features, [key]: e.target.checked }
                              })}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">{label}</Typography>}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {createdToken ? (
              <Button onClick={() => setCreateDialogOpen(false)}>
                Fechar
              </Button>
            ) : (
              <>
                <Button onClick={() => setCreateDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateToken}
                  sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
                >
                  Gerar Token
                </Button>
              </>
            )}
          </DialogActions>
        </PlatformDialog>

        {/* Delete Confirmation Dialog (Padronizado) */}
        <PlatformDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <PlatformDialogTitle
            title="Confirmar Exclusão"
            subtitle="Esta chave de acesso será invalidada."
            icon={<DeleteIcon />}
            onClose={() => setDeleteDialogOpen(false)}
            color="error"
          />
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Tem certeza que deseja excluir o token de <strong>{selectedToken?.clientName}</strong>?
            </Typography>
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              Esta ação não pode ser desfeita e impedirá a ativação do produto.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={handleDeleteToken} sx={{ fontWeight: 700, borderRadius: 2 }}>
              Excluir Token
            </Button>
          </DialogActions>
        </PlatformDialog>
        {/* Edit Plan Dialog (Padronizado) */}
        <PlatformDialog 
          open={editPlanDialogOpen} 
          onClose={() => setEditPlanDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
        >
          <PlatformDialogTitle
            title={`Plano de ${selectedEditUser?.name}`}
            subtitle="As alterações entram em vigor imediatamente."
            icon={<EditIcon />}
            onClose={() => setEditPlanDialogOpen(false)}
          />
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {editPlanError && <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>{editPlanError}</Alert>}
              {editPlanSuccess && <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>{editPlanSuccess}</Alert>}

              <FormControl fullWidth size="small">
                <InputLabel>Pacote Selecionado</InputLabel>
                <Select
                  value={editPlan.planName}
                  label="Pacote Selecionado"
                  onChange={(e) => {
                    const preset = PLAN_PRESETS[e.target.value] || {};
                    setEditPlan(prev => ({
                      ...prev,
                      planName: e.target.value,
                      ...preset,
                      features: mergePackageFeatures(preset.features || prev.features, preset.hasAI ?? prev.hasAI),
                      miaFeatures: preset.hasAI !== undefined
                        ? normalizeMIAFeaturesForForm({}, preset.hasAI)
                        : normalizeMIAFeaturesForForm(prev.miaFeatures, prev.hasAI)
                    }));
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Starter">Starter — 100 pub/mês · 1 marca · R$ 397/mês</MenuItem>
                  <MenuItem value="Pro">Pro — 300 pub/mês · 3 marcas · R$ 797/mês</MenuItem>
                  <MenuItem value="Business">Business — 700 pub/mês · 7 marcas · R$ 1.397/mês</MenuItem>
                  <MenuItem value="Enterprise">Enterprise — 2.000 pub/mês · 15 marcas · R$ 2.197/mês</MenuItem>
                  <MenuItem value="Custom">Custom — Configurar manualmente</MenuItem>
                </Select>
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Máx. Links" type="number"
                    value={editPlan.maxLinks}
                    onChange={(e) => setEditPlan(p => ({ ...p, maxLinks: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Máx. Marcas" type="number"
                    value={editPlan.maxBrands}
                    onChange={(e) => setEditPlan(p => ({ ...p, maxBrands: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Estender (dias)" type="number"
                    value={editPlan.durationDays}
                    onChange={(e) => setEditPlan(p => ({ ...p, durationDays: e.target.value }))}
                    helperText="0 = manter expiração atual"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editPlan.hasAI}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEditPlan(p => ({
                            ...p,
                            hasAI: checked,
                            features: {
                              ...p.features,
                              aiQualitativeAnalysis: checked,
                              briefingAssistant: checked
                            },
                            miaFeatures: normalizeMIAFeaturesForForm(p.miaFeatures, checked)
                          }));
                        }}
                      />
                    }
                    label={<Typography variant="body2">Acesso à IA</Typography>}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Módulos do pacote contratado
                    </Typography>
                    <Grid container spacing={1}>
                      {CONTRACT_FEATURE_OPTIONS.map(({ key, label }) => (
                        <Grid item xs={12} sm={6} key={key}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={editPlan.features?.[key] === true}
                                onChange={(e) => setEditPlan(p => ({
                                  ...p,
                                  features: {
                                    ...p.features,
                                    [key]: e.target.checked
                                  }
                                }))}
                              />
                            }
                            label={<Typography variant="body2">{label}</Typography>}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
                {editPlan.hasAI && (
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Recursos MIA liberados
                      </Typography>
                      <Grid container spacing={1}>
                        {MIA_FEATURE_OPTIONS.map(({ key, label }) => (
                          <Grid item xs={12} sm={6} key={key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={editPlan.miaFeatures?.[key] === true}
                                  onChange={(e) => setEditPlan(p => ({
                                    ...p,
                                    miaFeatures: {
                                      ...p.miaFeatures,
                                      [key]: e.target.checked
                                    }
                                  }))}
                                />
                              }
                              label={<Typography variant="body2">{label}</Typography>}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField 
                    fullWidth size="small" label="Observação do Log" multiline rows={2}
                    value={editPlan.notes}
                    onChange={(e) => setEditPlan(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Controle interno de alteração de plano..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
            <Button onClick={() => setEditPlanDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleSaveEditPlan}
              disabled={editPlanLoading}
              startIcon={editPlanLoading ? <CircularProgress size={16} color="inherit" /> : <UpgradeIcon />}
              sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
            >
              Atualizar Plano
            </Button>
          </DialogActions>
        </PlatformDialog>

        {/* ===== Usuários com Plano Ativo ===== */}
        <Box sx={{ mt: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Usuários com Plano Ativo</Typography>
              <Chip label={activatedUsers.length} size="small" color="primary" />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Buscar usuário..."
                value={activatedUsersSearch}
                onChange={(e) => {
                  setActivatedUsersSearch(e.target.value);
                  fetchActivatedUsers(e.target.value);
                }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 220 }}
              />
              <Tooltip title="Atualizar">
                <IconButton size="small" onClick={() => fetchActivatedUsers(activatedUsersSearch)}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {activatedUsersLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : activatedUsers.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Nenhum usuário com plano ativo encontrado</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: 'grey.900' } }}>
                    <TableCell>Usuário</TableCell>
                    <TableCell>Plano</TableCell>
                    <TableCell align="center">Pub/mês</TableCell>
                    <TableCell align="center">Uso (mês atual)</TableCell>
                    <TableCell>Válido até</TableCell>
                    <TableCell align="center">IA</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activatedUsers.map((u) => {
                    const expiry = u.plan.planExpiresAt ? new Date(u.plan.planExpiresAt) : null;
                    const isExpired = expiry && expiry < new Date();
                    const usagePct = u.plan.maxLinks > 0 ? Math.round((u.usage.monthlyLinks / u.plan.maxLinks) * 100) : 0;
                    return (
                      <TableRow key={u._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={u.plan.name} size="small" color="secondary" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{u.plan.maxLinks}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color={usagePct >= 90 ? 'error.main' : 'text.primary'}>
                            {u.usage.monthlyLinks} / {u.plan.maxLinks} ({usagePct}%)
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {expiry ? (
                            <Typography variant="body2" color={isExpired ? 'error.main' : 'success.main'}>
                              {format(expiry, 'dd/MM/yyyy', { locale: ptBR })}
                              {isExpired && ' (expirado)'}
                            </Typography>
                          ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          {u.plan.hasAI ? <ActiveIcon fontSize="small" color="success" /> : <RevokedIcon fontSize="small" color="disabled" />}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Editar Plano">
                            <IconButton size="small" color="primary"
                              onClick={() => {
                                setSelectedEditUser({ userId: u._id.toString(), name: u.name, email: u.email });
                                setEditPlan({
                                  planName: u.plan.name || 'Starter',
                                  maxLinks: u.plan.maxLinks,
                                  maxBrands: u.plan.maxBrands,
                                  hasAI: u.plan.hasAI,
                                  durationDays: 0,
                                  features: mergePackageFeatures(u.plan.features, u.plan.hasAI),
                                  miaFeatures: normalizeMIAFeaturesForForm(u.plan.miaFeatures, u.plan.hasAI),
                                  notes: ''
                                });
                                setEditPlanSuccess('');
                                setEditPlanError('');
                                setEditPlanDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revogar Plano">
                            <IconButton size="small" color="error"
                              onClick={() => {
                                setRevokeTarget({ userId: u._id.toString(), name: u.name });
                                setRevokeError('');
                                setRevokeDialogOpen(true);
                              }}
                            >
                              <PersonOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Dialog: Revogação de Plano (Padronizado) */}
        <PlatformDialog 
          open={revokeDialogOpen} 
          onClose={() => setRevokeDialogOpen(false)} 
          maxWidth="xs" 
          fullWidth
        >
          <PlatformDialogTitle
            title="Revogar Plano?"
            subtitle={revokeTarget?.name}
            icon={<PersonOffIcon />}
            onClose={() => setRevokeDialogOpen(false)}
            color="error"
          />
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Isso vai desativar o acesso do usuário ao MIA imediatamente, zerando limites de marcas e links.
            </Typography>
            {revokeError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{revokeError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
            <Button onClick={() => setRevokeDialogOpen(false)} disabled={revokeLoading} sx={{ fontWeight: 600 }}>
              Cancelar
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleRevokePlan}
              disabled={revokeLoading}
              startIcon={revokeLoading ? <CircularProgress size={16} color="inherit" /> : <PersonOffIcon />}
              sx={{ fontWeight: 700, borderRadius: 2 }}
            >
              Revogar Acesso
            </Button>
          </DialogActions>
        </PlatformDialog>

      </Box>
    </AdminLayout>
  );
};

export default MIATokenManagementPage;
